import { memberIdentityOf, endpointTargetIdentity } from './memberIdentity';

// KEEPING A MEMBER'S ROW IN STEP WITH THE PERSON-TAGS ENDPOINT.
//
// Person tags are written by their own endpoint, POST /pedigree/person-tags,
// never by the pedigree save. But PedigreeChart's autosave, POST
// /pedigree-config, $sets every non-proband member's WHOLE row from
// memberRecords, personTags included. So a tags write that does not also land
// on the member's row in memberRecords is undone by the next unrelated edit in
// the family: the autosave writes the row's old tags back over it.
//
// Every writer to that endpoint therefore updates the row as well, through
// these helpers, and with a bare setMemberRecords that does NOT move the save
// status to IN_PROGRESS. The autosave's scheduler runs on the change, but
// planSaveAttempt sends nothing while the status is SAVED, so no second
// pedigree POST goes out and no family lock is taken for it. The row is
// updated BEFORE the request, so a pedigree POST that is already pending and
// fires while the request is in flight carries the new tags, and rolled back
// if the endpoint refuses.
//
// Rows are matched on memberIdentityOf (`_id`, never the pid). Each helper
// returns the same array when nothing changed, so a no-op costs no re-render
// and no autosave attempt.

/**
 * Apply `update(currentTags)` to the personTags of the row for `identity`.
 */
export const updatePersonTagsRow = (records, identity, update) => {
  if (!Array.isArray(records) || identity == null) return records;
  let changed = false;
  const next = records.map((m) => {
    if (memberIdentityOf(m) !== identity) return m;
    const personTags = update(m.personTags);
    if (personTags === m.personTags) return m;
    changed = true;
    return { ...m, personTags };
  });
  return changed ? next : records;
};

/**
 * Replace the row's tags, as the endpoint's default mode does. With
 * `expected`, only a row still holding exactly that array is changed, which is
 * how a rollback avoids undoing something that replaced the row meanwhile.
 */
export const withPersonTags = (records, identity, personTags, expected) =>
  updatePersonTagsRow(records, identity, (current) =>
    expected !== undefined && current !== expected ? current : personTags
  );

/**
 * The endpoint's `append: true` mode, exactly (backend/routes/pedigree.js,
 * POST /person-tags): every stored tag is kept, and an addition is appended
 * unless a stored tag already carries its label (exact, case-sensitive match
 * on `tag`). Additions are not de-duplicated against each other, because the
 * server does not do that either. Returns `current` itself when nothing is
 * added.
 */
export const appendPersonTags = (current, additions) => {
  const stored = Array.isArray(current) ? current : [];
  const storedLabels = stored.map((t) => t?.tag);
  const added = (additions || []).filter((t) => !storedLabels.includes(t.tag));
  return added.length ? stored.concat(added) : current;
};

/** The row side of an append. */
export const withAppendedPersonTags = (records, identity, additions) =>
  updatePersonTagsRow(records, identity, (current) =>
    appendPersonTags(current, additions)
  );

/**
 * Undo an append: remove exactly the tag OBJECTS it inserted, by reference. A
 * tag that was already stored was never inserted, and a row that has since
 * been replaced (a save echoed back, the family reloaded) holds other objects,
 * so neither is touched.
 */
export const withoutPersonTags = (records, identity, inserted) =>
  updatePersonTagsRow(records, identity, (current) => {
    if (!Array.isArray(current)) return current;
    const next = current.filter((t) => !inserted.includes(t));
    return next.length === current.length ? current : next;
  });

/**
 * The identity of the row the endpoint writes to, chosen the way the endpoint
 * chooses it: the proband's working record when `isProband`, otherwise the
 * member with that `familyMemberId`. Null when this family holds no such row.
 * The rule is shared with the genotype variant push (memberIdentity.js).
 */
export const personTagsTargetIdentity = endpointTargetIdentity;
